import {findCategoryById} from '@frontend/helpers/findCategoryById'
import type {
    FilteredItemsPageUrlQueryParams,
    ItemsListRouteParams,
} from '@frontend/modules/public/ads/AdsModuleTypes'
import {LocalStorage} from '@frontend/services/LocalStorage'
import {
    useAdsStore,
    useAuthStore,
} from '@frontend/stores'
import {useEffect} from 'react'
import {useParams} from 'react-router-dom'
import {useUrlQueryParams} from 'swayok-react-mdb-ui-kit/helpers/useUrlQueryParams'
import {extractIdFromUrlSlug} from '../../../../helpers/extractIdFromUrlSlug'
import {findLocationBySlugWithId} from '../../../../helpers/findLocationBySlugWithId'

// Хук для инициализации страницы с отфильтрованными объявлениями и услугами.
// Возвращает готовность к отображению данных (isReady).
export function useInitFilteredItemsPage(): boolean {
    const loadingUserData = useAuthStore(
        state => state.loadingUserData
    )
    const categories = useAdsStore(
        state => state.categories
    )
    const locations = useAdsStore(
        state => state.locations
    )
    const setSearchPanelKeywords = useAdsStore(
        state => state.setSearchPanelKeywords
    )
    const setSearchPanelLocation = useAdsStore(
        state => state.setSearchPanelLocation
    )
    const setSearchPanelCategory = useAdsStore(
        state => state.setSearchPanelCategory
    )

    // Данные о текущем URL.
    const {
        urlQueryParams,
    } = useUrlQueryParams<FilteredItemsPageUrlQueryParams>()
    const routeParams = useParams<ItemsListRouteParams>()

    // Обновление данных в useAdsStore() и LocalStorage из URL query.
    useEffect(() => {
        setSearchPanelKeywords(urlQueryParams.get('keywords') ?? '')
        const locationSlugWithId = urlQueryParams.get('location')
        // Значение locationSlugWithId может быть 0.
        if (locationSlugWithId !== null && locationSlugWithId.trim() !== '') {
            LocalStorage.setFilterLocation(locationSlugWithId)
        }
        // При демонтаже сбрасываем категорию.
        return () => {
            setSearchPanelCategory(null)
        }
    }, [])

    // Задать/сменить локацию в панели поиска, если она указана в URL query.
    useEffect(
        () => {
            if (locations.tree.length > 0) {
                const locationSlugWithId = urlQueryParams.get('location')
                // Значение locationSlugWithId может быть 0.
                if (locationSlugWithId && locationSlugWithId.trim() !== '') {
                    const location = findLocationBySlugWithId(locations.tree, locationSlugWithId)
                    LocalStorage.setFilterLocation(location?.slugWithId ?? null)
                    setSearchPanelLocation(location ?? null)
                }
            }
        },
        [
            locations.tree.length > 0,
            urlQueryParams.get('location'),
        ]
    )

    // Задать категорию в панели поиска, если она указана в URL params.
    useEffect(() => {
        if (categories.length > 0) {
            const categoryId = extractIdFromUrlSlug(routeParams.slugWithId)
            // Значение categoryId НЕ может быть 0.
            if (categoryId && categoryId > 0) {
                setSearchPanelCategory(
                    findCategoryById(categories, categoryId)
                )
            }
        }
    }, [categories.length > 0])

    return !loadingUserData
}
